import React, { ReactNode, RefObject } from 'react';
import { Field, Form, FormSpy } from 'react-final-form';
import { I18n, Translate } from 'react-redux-i18n';
import { FORM_ERROR } from 'final-form';
import TextInput from '../../common/inputs/TextInput';
import Button from '../../common/Button';
import logo from '../../../img/createAccount/identite.svg';
import { InscriptionDTO } from '../../../network/api/types/InscriptionDTO';
import creationApi from '../../../network/api/creationApi';
import { CountriesDTO } from '../../../network/api/types/CountriesDTO';
import AutocompleteInput from '../../common/inputs/autocompleteInput/AutocompleteInput';
import { SingleFieldDTOString } from '../../../network/api/types/SingleFieldDTOString';
import { CityDTO } from '../../../network/api/types/CityDTO';
import {
  validateCommuneDeNaissance,
  validateDateDeNaissance,
  validateNom,
} from '../../../services/utils';
import { validationMap } from '../../../network/api/types/compte/RequirementType';
import DateInput from '../../common/inputs/DateInput';

type Props = {
  divRef: RefObject<HTMLDivElement>;
  update: (values: InscriptionDTO) => void;
  onChange: (index: number) => void;
  initial: InscriptionDTO;
  openPhotoModal: () => void,
  photo: string;
  isEnrolement?: boolean,
};

type State = {
  cities: CityDTO[];
  countries: CountriesDTO;
};

class CreationIds extends React.Component<Props, State> {
  state: State = {
    cities: [],
    countries: {},
  };

  componentDidMount(): void {
    creationApi.fetchCountries()
      .then((countries) => this.setState({ countries }));
  }

  handleEdit = async (formValues: InscriptionDTO): Promise<{ [FORM_ERROR]: string }> => {
    try {
      const paysDeNaissance = this.state.countries.countries.filter((c) =>
        formValues.paysDeNaissanceLabel === c.nom)[0].code;

      let values:InscriptionDTO = {
        ...formValues,
        paysDeNaissance,
      };

      if (this.props.isEnrolement) {
        return creationApi
          .validateFromEnrolement(values)
          .then((response) => {
            values = {
              ...values,
              pseudo: response.message,
            };
            this.props.update(values);
          })
          .catch((err) => {
            if (typeof err.then === 'function') {
              return err.then((e) => ({ [FORM_ERROR]: e.fieldErrors[0].message }));
            }
            return null;
          });
      }

      return creationApi
        .isUserInfoValid(values)
        .then((response) => {
          values = {
            ...values,
            pseudo: response.message,
          };
          this.props.update(values);
        })
        .catch((err) => {
          if (typeof err.then === 'function') {
            return err.then((e) => ({ [FORM_ERROR]: e.fieldErrors[0].message }));
          }
          return null;
        });
    } catch (err) {
      return { [FORM_ERROR]: 'Le formulaire comporte une erreur. Veuillez vérifier les champs.' };
    }
  };

  getCities = (prefixCity: SingleFieldDTOString, pays: string): void => {
    const cities = [];
    if (pays !== 'FR') {
      this.setState({ cities });
      return;
    }
    if (pays === 'FR' && prefixCity.value.length > 1) {
      creationApi.fecthCities(prefixCity).then((data) => {
        data.cities.forEach((c) => {
          let code = c.code;
          if (code.length === 5) {
            code = code.slice(0, 2);
            if (code === '97' || code === '98') {
              code = c.code.slice(0, 3);
            }
          }
          const city = { nom: `${c.nom} (${code})`, code: c.code };
          cities.push(city);
        });
        this.setState({ cities });
      });
    }
  };

  render(): ReactNode {
    const { initial } = this.props;
    return (
      <div ref={this.props.divRef} className="account-activation-form-container creation-form-container">
        <div className="account-activation-col">
          <h3>
            <Translate value="CREATION.ENTER_ID" />
          </h3>
        </div>
        <div className="account-activation-col creation-form">
          <Form
            onSubmit={this.handleEdit}
            initialValues={initial}
            validate={(values) => {
              const errors = {
                nomUsuel: null,
                prenom: null,
                nomDeNaissance: null,
                dateDeNaissance: null,
                communeDeNaissance: null,
                paysDeNaissanceLabel: null,
              };
              if (!validateNom(values.nomUsuel)) {
                errors.nomUsuel = 'Le nom usuel est invalide. Il ne doit pas contenir de caractères spéciaux ou d\'espace.';
              }
              if (!validateNom(values.prenom)) {
                errors.prenom = 'Le prénom est invalide. Il ne doit pas contenir de caractères spéciaux ou d\'espace.';
              }
              if (!validateNom(values.nomDeNaissance)) {
                errors.nomDeNaissance = 'Le nom de naissance est invalide. Il ne doit pas contenir de caractères spéciaux ou d\'espace.';
              }
              if (values.dateDeNaissance && !validateDateDeNaissance(values.dateDeNaissance)) {
                errors.dateDeNaissance = 'La date de naissance est invalide.';
              }
              if (this.state.countries.countries
                && !this.state.countries.countries.some((c) =>
                  c.nom === values.paysDeNaissanceLabel)) {
                errors.paysDeNaissanceLabel = 'Veuillez sélectionner un pays dans la liste.';
              }
              if (values.paysDeNaissanceLabel === 'France'
                && this.state.cities.length > 0
                && !(this.state.cities.some((c) => c.nom === values.communeDeNaissance)
                  || validateCommuneDeNaissance(values.communeDeNaissance))) {
                errors.communeDeNaissance = 'Veuillez sélectionner une commune dans la liste.';
              }
              if (values.paysDeNaissanceLabel !== 'France'
                && values.communeDeNaissance
                && (!validationMap.latinPattern(values.communeDeNaissance)
                  || validationMap.numeric(values.communeDeNaissance)
                  || values.communeDeNaissance.length > 163)) {
                errors.communeDeNaissance = 'Le nom de la commune de naissance ne doit pas contenir de caractères numériques.';
              }
              if (!errors.nomUsuel
                && !errors.prenom
                && !errors.nomDeNaissance
                && !errors.dateDeNaissance
                && !errors.paysDeNaissanceLabel
                && !errors.communeDeNaissance) {
                return {};
              }
              return errors;
            }}
            render={(
              {
                handleSubmit,
                submitError,
                values,
                form,
                submitting,
              },
            ) => (
              <form className="connection-information-form" onSubmit={handleSubmit}>
                <FormSpy
                  subscription={{
                    values: true, pristine: true, initialValues: true, touched: true,
                  }}
                  onChange={(state) => {
                    if (state.values.civilite !== state.initialValues.civilite
                      || state.values.prenom !== state.initialValues.prenom
                        || state.values.nomUsuel !== state.initialValues.nomUsuel
                        || state.values.nomDeNaissance !== state.initialValues.nomDeNaissance
                        || state.values.communeDeNaissance
                        !== state.initialValues.communeDeNaissance
                        || state.values.paysDeNaissanceLabel
                        !== state.initialValues.paysDeNaissanceLabel
                      || state.values.dateDeNaissance
                      !== state.initialValues.dateDeNaissance
                    ) {
                      this.props.onChange(0);
                    }
                  }}
                />
                <div className="big-button-radio-container">
                  <div className="profile-img">
                    <div className="img-container">
                      <img
                        className="profile-picture"
                        src={this.props.photo}
                        alt="profile"
                      />
                      <button
                        type="button"
                        className="btn-add"
                        onClick={this.props.openPhotoModal}
                      >
                        <i className="fa fa-plus" />
                      </button>
                    </div>
                  </div>
                  <div className="civilite-container">
                    <div className="civilite-label">
                      <span>Civilité</span>
                    </div>
                    <div className="civilite-radio-buttons">
                      <Field
                        type="radio"
                        name="civilite"
                        value="Monsieur"
                        required
                        render={({ input }) => (
                          <label htmlFor="monsieur" className="big-button-radio">
                            <input
                              type="radio"
                              name={input.name}
                              id="monsieur"
                              value="Monsieur"
                              checked={input.checked}
                              onChange={input.onChange}
                            />
                            <span className="button-customize" />
                            <Translate value="EMERGENCY_INFORMATION.MONSIEUR" />
                          </label>
                        )}
                      />
                      <Field
                        type="radio"
                        name="civilite"
                        value="Madame"
                        render={({ input }) => (
                          <label htmlFor="madame" className="big-button-radio">
                            <input
                              type="radio"
                              name={input.name}
                              id="madame"
                              value="Madame"
                              checked={input.checked}
                              onChange={input.onChange}
                            />
                            <span className="button-customize" />
                            <Translate value="EMERGENCY_INFORMATION.MADAME" />
                          </label>
                        )}
                      />
                    </div>
                  </div>
                </div>
                <Field
                  name="prenom"
                  component={TextInput}
                  placeholder={I18n.t('CREATION.FIRSTNAME')}
                  type="text"
                  required
                />
                <Field
                  name="nomUsuel"
                  component={TextInput}
                  placeholder={I18n.t('CREATION.NAME')}
                  type="text"
                  required
                />
                <Field
                  name="nomDeNaissance"
                  component={TextInput}
                  placeholder={I18n.t('CREATION.BIRTHNAME')}
                  type="text"
                  required
                />
                <Field
                  name="dateDeNaissance"
                  component={DateInput}
                  placeholder={I18n.t('CREATION.BIRTHDATE')}
                  type="text"
                  fieldType="date"
                  required={!this.props.isEnrolement}
                />
                <div className="password-container">
                  <Field
                    type="text"
                    name="paysDeNaissanceLabel"
                    component={AutocompleteInput}
                    placeholder={I18n.t('CREATION.BIRTH_COUNTRY')}
                    selectSources={
                      this.state.countries.countries
                      && this.state.countries.countries.map((paysDeNaissance) => ({
                        label: paysDeNaissance.nom,
                        id: paysDeNaissance.code,
                      }))
                    }
                    onChangeInputValue={(id) => form.change('paysDeNaissance', id)}
                    required
                  />
                  <Field
                    type="text"
                    name="communeDeNaissance"
                    component={AutocompleteInput}
                    placeholder={I18n.t('CREATION.BIRTH_CITY')}
                    selectSources={
                      this.state.cities.length > 0
                      && this.state.cities.map((city) => ({
                        label: city.nom,
                        id: city.code,
                      }))
                    }
                    onChangeSelectSources={(e) => this.getCities(
                      { value: e.target.value }, values.paysDeNaissance,
                    )}
                  />

                </div>
                {submitError && <div className="error">{submitError}</div>}
                <div className="button-container">
                  <Button
                    type="primary"
                    buttonType="submit"
                    i18nKey="actions.NEXT"
                    isLoading={submitting}
                    disabled={values.civilite === null
                    || values.nomUsuel === null
                    || values.prenom === null
                    || values.nomDeNaissance === null
                    || (!this.props.isEnrolement && values.dateDeNaissance === null)
                    || values.paysDeNaissanceLabel === null}
                  />
                </div>
              </form>
            )}
          />
        </div>
        <div className="account-activation-col">
          <div>
            <img className="logo" src={logo} alt="logo" />
          </div>
          <Translate value="CREATION.UNIQUE_ID" />
        </div>
      </div>
    );
  }
}

export default CreationIds;
